import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TtqSession } from '../../services/ttqService';
import {
  applyDerivedFocusState,
  buildTtqibNativeCanvasModel,
  measureParticleCard,
  mintSegmentOnSelect,
  moveParticle,
  reflowStateIfNeeded,
  setActiveLayer,
  syncSessionLayers,
  updateViewport,
} from './ttqibCanvasEngine';
import { loadLocalTtqibState, saveLocalTtqibState } from './ttqibStateStore';
import { computeDefaultColumnViewport, isViewportUsable, TTQIB_BIRDS_EYE_EXIT_ZOOM_THRESHOLD, TTQIB_ZOOM_MAX } from './ttqibViewport';
import { computeFocusCardViewport } from './ttqibFocusViewport';
import { ensureParticleNumbers, particleNumbersEqual } from './ttqibParticleNumbers';
import type { TtqDerivedFocus } from './ttqibDerivedStack';
import {
  upsertAssociations,
  type TtqParticleAssociation,
} from './ttqibAssociationLayout';
import type { TtqibNativeCanvasModel, TtqibState, TtqibViewport } from './types';
import { ttqService } from '../../services/ttqService';

function contentSig(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    const text = value.trim();
    return `${text.length}:${text.slice(0, 32)}`;
  }
  try {
    const text = JSON.stringify(value);
    return `${text.length}:${text.slice(0, 32)}`;
  } catch {
    return 'x';
  }
}

function buildLayoutFingerprint(session: TtqSession): string {
  const particleSig = (session.particles ?? []).map((entry) => [
    entry.particleId,
    entry.pipelineStatus,
    contentSig(entry.particleTitle),
    contentSig(entry.particleQuestions),
    contentSig(entry.particleAnswers),
  ].join(':')).join('|');
  const pathRunSig = (session.pathRuns ?? []).map((run) => [
    run.id,
    run.status,
    run.sourceParticleId,
    run.questionChainVariantId || '',
    run.expansionProvenance?.anchorText || '',
    ...(run.derivedParticleIds ?? []),
  ].join(':')).join('|');
  return `${particleSig}::${pathRunSig}`;
}

export function useTtqibLayout(session: TtqSession | null) {
  const [canvasModel, setCanvasModel] = useState<TtqibNativeCanvasModel | null>(null);
  const [derivedFocus, setDerivedFocus] = useState<TtqDerivedFocus | null>(null);
  /** 递增后清除收敛卡位置锁，强制 placeTtqConvergeLodAnchors 重算 */
  const [convergeRelayoutNonce, setConvergeRelayoutNonce] = useState(0);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const derivedFocusRef = useRef(derivedFocus);
  derivedFocusRef.current = derivedFocus;
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remoteSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef<TtqibState | null>(null);

  const sessionId = session?.id;
  const layoutFingerprint = session ? buildLayoutFingerprint(session) : '';

  const persistState = useCallback((state: TtqibState) => {
    if (!sessionId) return;
    stateRef.current = state;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveLocalTtqibState(sessionId, state);
    }, 400);
    if (remoteSaveTimerRef.current) clearTimeout(remoteSaveTimerRef.current);
    remoteSaveTimerRef.current = setTimeout(() => {
      void ttqService.patchTtqibState(sessionId, {
        viewport: state.viewport,
        viewportInitialized: state.viewportInitialized,
        particleNumbers: state.particleNumbers,
        birdsEyePinnedFloats: state.birdsEyePinnedFloats ?? [],
        associations: state.associations ?? [],
        updatedAt: state.updatedAt,
      }).catch((error) => {
        console.warn('[ttqib] remote ttqibState save failed', error);
      });
    }, 900);
  }, [sessionId]);

  /** 离开页面前立刻落盘视口，避免 debounce 未触发导致返回后视口丢失 */
  const flushPersistNow = useCallback(() => {
    if (!sessionId || !stateRef.current) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    saveLocalTtqibState(sessionId, stateRef.current);
  }, [sessionId]);

  useEffect(() => {
    const currentSession = sessionRef.current;
    if (!currentSession) {
      setCanvasModel(null);
      return;
    }

    const focus = derivedFocusRef.current;
    const localState = sessionId ? loadLocalTtqibState(sessionId, currentSession) : null;
    const serverState = currentSession.ttqibState && typeof currentSession.ttqibState === 'object'
      ? currentSession.ttqibState as Partial<TtqibState>
      : null;
    const serverRelayoutAt = Date.parse(String(serverState?.postMergeRelayoutAt || ''));
    const localRelayoutAt = Date.parse(String(localState?.postMergeRelayoutAt || ''));
    const preferServerAfterMerge = Number.isFinite(serverRelayoutAt)
      && (!Number.isFinite(localRelayoutAt) || serverRelayoutAt > localRelayoutAt);
    // 合并后重布局：优先服务端；否则本地优先，本地无鸟瞰钉住时回退服务端
    const mergedSeed: Partial<TtqibState> | null = preferServerAfterMerge
      ? (serverState ? { ...serverState } : null)
      : (localState || serverState
        ? {
          ...(serverState ?? {}),
          ...(localState ?? {}),
          birdsEyePinnedFloats: (localState?.birdsEyePinnedFloats?.length
            ? localState.birdsEyePinnedFloats
            : serverState?.birdsEyePinnedFloats) ?? [],
        }
        : null);
    const savedState = mergedSeed;
    const synced = syncSessionLayers(
      currentSession,
      buildTtqibNativeCanvasModel(currentSession, savedState, focus).state,
    );
    const reflowed = reflowStateIfNeeded(currentSession, synced);
    let nextState = applyDerivedFocusState(currentSession, reflowed, focus);
    const numbered = ensureParticleNumbers(currentSession, nextState.particleNumbers);
    if (!particleNumbersEqual(nextState.particleNumbers, numbered)) {
      nextState = {
        ...nextState,
        particleNumbers: numbered,
        updatedAt: new Date().toISOString(),
      };
    } else if (!nextState.particleNumbers) {
      nextState = { ...nextState, particleNumbers: numbered };
    }
    const model = buildTtqibNativeCanvasModel(currentSession, nextState, focus);

    const shouldInitDefault = !nextState.viewportInitialized || !isViewportUsable(nextState.viewport);

    if (shouldInitDefault && model.objects.length > 0) {
      const wholes = model.objects.filter((item) => item.type === 'particle-whole');
      const containerWidth = typeof window !== 'undefined' ? Math.max(window.innerWidth - 32, 640) : 1200;
      const containerHeight = typeof window !== 'undefined' ? Math.max(window.innerHeight - 96, 480) : 800;
      const initial = computeDefaultColumnViewport(
        wholes.length ? wholes : model.objects,
        containerWidth,
        containerHeight,
        2.5,
      );
      nextState = updateViewport(nextState, initial);
      persistState(nextState);
      setCanvasModel(buildTtqibNativeCanvasModel(currentSession, nextState, focus));
      return;
    }

    persistState(nextState);
    setCanvasModel(model);
  }, [derivedFocus, layoutFingerprint, persistState, sessionId]);

  useEffect(() => {
    stateRef.current = canvasModel?.state ?? null;
  }, [canvasModel]);

  useEffect(() => {
    const onLeave = () => {
      if (document.visibilityState === 'hidden') flushPersistNow();
    };
    window.addEventListener('pagehide', flushPersistNow);
    window.addEventListener('visibilitychange', onLeave);
    return () => {
      window.removeEventListener('pagehide', flushPersistNow);
      window.removeEventListener('visibilitychange', onLeave);
    };
  }, [flushPersistNow]);

  useEffect(() => () => {
    flushPersistNow();
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (remoteSaveTimerRef.current) clearTimeout(remoteSaveTimerRef.current);
  }, [flushPersistNow]);

  const setViewport = useCallback((viewport: TtqibViewport) => {
    setCanvasModel((current) => {
      if (!current || !sessionRef.current) return current;
      const nextState = updateViewport(current.state, viewport);
      persistState(nextState);
      return buildTtqibNativeCanvasModel(sessionRef.current, nextState, derivedFocusRef.current);
    });
  }, [persistState]);

  const moveParticleCard = useCallback((particleId: string, position: { x: number; y: number }) => {
    setCanvasModel((current) => {
      if (!current || !sessionRef.current) return current;
      const nextState = moveParticle(
        current.state,
        particleId,
        position,
        sessionRef.current,
        derivedFocusRef.current,
      );
      persistState(nextState);
      return buildTtqibNativeCanvasModel(sessionRef.current, nextState, derivedFocusRef.current);
    });
  }, [persistState]);

  const switchLayer = useCallback((layerId: string) => {
    setCanvasModel((current) => {
      if (!current || !sessionRef.current) return current;
      const nextState = setActiveLayer(current.state, layerId);
      persistState(nextState);
      return buildTtqibNativeCanvasModel(sessionRef.current, nextState, derivedFocusRef.current);
    });
  }, [persistState]);

  const measureParticle = useCallback((
    particleId: string,
    size: { width: number; height: number },
  ) => {
    setCanvasModel((current) => {
      if (!current || !sessionRef.current) return current;
      const nextState = measureParticleCard(
        current.state,
        particleId,
        size,
        sessionRef.current,
        derivedFocusRef.current,
      );
      if (nextState === current.state) return current;
      persistState(nextState);
      return buildTtqibNativeCanvasModel(sessionRef.current, nextState, derivedFocusRef.current);
    });
  }, [persistState]);

  const selectObject = useCallback((objectId: string | null) => {
    setCanvasModel((current) => {
      if (!current || !objectId || !sessionRef.current) return current;
      const nextState = mintSegmentOnSelect(current.state, objectId);
      persistState(nextState);
      return buildTtqibNativeCanvasModel(sessionRef.current, nextState, derivedFocusRef.current);
    });
  }, [persistState]);

  /** 将指定颗粒整卡放大并居中到视口（新衍生 / Scan） */
  const centerParticle = useCallback((particleId: string) => {
    setCanvasModel((current) => {
      if (!current || !sessionRef.current) return current;
      const whole = current.objects.find((item) => (
        item.type === 'particle-whole'
        && item.particleId === particleId
        && !item.hidden
      ));
      if (!whole) return current;
      const viewW = typeof window !== 'undefined' ? Math.max(window.innerWidth, 640) : 1200;
      const viewH = typeof window !== 'undefined' ? Math.max(window.innerHeight - 56, 480) : 800;
      const cardW = whole.width ?? 420;
      const cardH = whole.height ?? 400;
      const nextState = updateViewport(
        current.state,
        computeFocusCardViewport(
          { x: whole.x, y: whole.y, width: cardW, height: cardH },
          viewW,
          viewH,
          {
            minZoom: TTQIB_BIRDS_EYE_EXIT_ZOOM_THRESHOLD,
            maxZoom: TTQIB_ZOOM_MAX,
          },
        ),
      );
      persistState(nextState);
      return buildTtqibNativeCanvasModel(sessionRef.current, nextState, derivedFocusRef.current);
    });
  }, [persistState]);

  const setBirdsEyePinnedFloats = useCallback((
    floats: Array<{ particleId: string; left: number; top: number }>,
  ) => {
    setCanvasModel((current) => {
      if (!current || !sessionRef.current) return current;
      const prev = JSON.stringify(current.state.birdsEyePinnedFloats ?? []);
      const nextKey = JSON.stringify(floats);
      if (prev === nextKey) return current;
      const nextState: TtqibState = {
        ...current.state,
        birdsEyePinnedFloats: floats,
        updatedAt: new Date().toISOString(),
      };
      persistState(nextState);
      return buildTtqibNativeCanvasModel(sessionRef.current, nextState, derivedFocusRef.current);
    });
  }, [persistState]);

  const addAssociations = useCallback((
    sourceParticleId: string,
    linkedParticleIds: string[],
  ) => {
    setCanvasModel((current) => {
      if (!current || !sessionRef.current) return current;
      const associations = upsertAssociations(
        current.state.associations ?? [],
        sourceParticleId,
        linkedParticleIds,
      );
      const nextState = applyDerivedFocusState(
        sessionRef.current,
        {
          ...current.state,
          associations,
          updatedAt: new Date().toISOString(),
        },
        derivedFocusRef.current,
      );
      persistState(nextState);
      return buildTtqibNativeCanvasModel(sessionRef.current, nextState, derivedFocusRef.current);
    });
  }, [persistState]);

  /** 颗粒纵列重排 + 收敛 LOD 落位重算（清位置锁） */
  const relayoutCards = useCallback(() => {
    setCanvasModel((current) => {
      if (!current || !sessionRef.current) return current;
      const nextState = applyDerivedFocusState(
        sessionRef.current,
        current.state,
        derivedFocusRef.current,
      );
      persistState(nextState);
      return buildTtqibNativeCanvasModel(sessionRef.current, nextState, derivedFocusRef.current);
    });
    setConvergeRelayoutNonce((n) => n + 1);
  }, [persistState]);

  return useMemo(() => ({
    canvasModel,
    derivedFocus,
    setDerivedFocus,
    setViewport,
    moveParticle: moveParticleCard,
    switchLayer,
    selectObject,
    centerParticle,
    measureParticle,
    addAssociations,
    setBirdsEyePinnedFloats,
    relayoutCards,
    convergeRelayoutNonce,
    associations: (canvasModel?.state.associations ?? []) as TtqParticleAssociation[],
  }), [
    addAssociations,
    canvasModel,
    centerParticle,
    convergeRelayoutNonce,
    derivedFocus,
    measureParticle,
    moveParticleCard,
    relayoutCards,
    selectObject,
    setBirdsEyePinnedFloats,
    setViewport,
    switchLayer,
  ]);
}
